/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { AnnotationTypes } from '@atlaskit/adf-schema';
import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
import { xcss, Box, Text, Flex } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import PageIcon from '@atlaskit/icon/core/page';

import { useUnreadCommentsActions } from '@confluence/unread-comments';
import type { CommentLocation } from '@confluence/inline-comments-queries';
import type { PageMode } from '@confluence/page-utils/entry-points/enums';
import { CommentType, CommentActionType, type CommentData } from '@confluence/comments-data';
import { fg } from '@confluence/feature-gating';
import { useInlineCommentQueryParams } from '@confluence/comment';
import { usePageInfo } from '@confluence/page-info';
import type { FlagsStateContainer } from '@confluence/flags';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { useAddCommentPermissionCheck, usePageClass } from '@confluence/comments-hooks';
import {
	useCommentsPanel,
	useCommentsPanelScroll,
	ViewValues,
} from '@confluence/comments-panel-utils';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker';
import { useCommentService } from '@confluence/content-types-bridge-comments';

import {
	type CommentThreadMouseEnterParams,
	handleCommentThreadMouseEnter,
	handleCommentThreadMouseLeave,
	handleCommentThreadSelection,
} from '../helper/commentThreadHelper';

import { CommentReplies } from './CommentReplies';
import { Comment } from './Comment';

export type CommentThreadProps = {
	parentComment: CommentData & {
		isFirstPageComment?: boolean;
	};
	supportedTopLevelActions: CommentAction[];
	eventEmitter: AnnotationUpdateEmitter;
	pageMode: PageMode;
	flags?: FlagsStateContainer;
	shouldRenderCommentThreadSeperator?: boolean;
	shouldRenderCommentTypeSeparator?: boolean;
};

const dividerStyle = xcss({
	borderBottom: `1px solid ${token('color.border')}`,
	position: 'relative',
	top: 'space.200',
});

const commentThreadBoxStyles = xcss({
	listStyle: 'none',
	position: 'relative',
	paddingLeft: 'space.200',
	paddingRight: 'space.250',
	paddingTop: 'space.200',
	paddingBottom: 'space.200',
	margin: '0',
	display: 'flex',
	flexDirection: 'column',
});

const newCommentThreadBoxStyles = xcss({
	cursor: 'pointer',
});

const commentTypeSeperatorStyle = xcss({
	display: 'flex',
	gap: 'space.050',
	paddingTop: 'space.300',
	paddingRight: 'space.300',
	paddingBottom: 'space.150',
	paddingLeft: 'space.200',
	borderTop: `1px solid ${token('color.border')}`,
});

const commentBodyBoxStyles = xcss({
	listStyle: 'none',
	position: 'relative',
	marginRight: 'space.100',
});

const isCurrentSelectedCommentStyles = xcss({
	background: token('color.background.selected'),
});

const isCurrentUserSelectedCommentStyles = xcss({
	background: token('color.background.input.hovered'),
});

const isThreadHoveredStyles = xcss({
	background: token('elevation.surface.hovered'),
});

const isThreadPressedStyles = xcss({
	background: token('color.background.input.hovered'),
});

const i18n = defineMessages({
	commentTypeSeperatorText: {
		id: 'comments-panel.comment.thread.type-seperator',
		defaultMessage: 'General comments',
		description: 'Text for the seperator of inline and general comment section',
	},
	contentAriaLabelWithName: {
		id: 'comments-panel.comment.thread.content.aria-label.with-name',
		defaultMessage: '{name}’s comment thread',
		description:
			'Aria-label for content associated with the comment thread, with the first comment author name',
	},
	anonymous: {
		id: 'comments-panel.comment.thread.display.name.anonymous',
		defaultMessage: 'Anonymous',
		description: 'Text used in place of a missing username',
	},
});

// This component contains parent comment and its replies
export const CommentThread = ({
	parentComment,
	supportedTopLevelActions,
	eventEmitter,
	pageMode,
	flags,
	shouldRenderCommentThreadSeperator,
	shouldRenderCommentTypeSeparator,
}: CommentThreadProps) => {
	const { formatMessage } = useIntl();
	const pageClass = usePageClass();

	const [
		{ currentlySelectedThreadKey, commentsMarkedAsRead, currentView },
		{ setCurrentlySelectedThreadKey, setCommentsInThreadToBeMarkedAsRead },
	] = useCommentsPanel();
	const { pageInfo } = usePageInfo({
		fetchPolicy: fg('confluence-comment-pageInfo-use-cache-first')
			? 'cache-first'
			: 'cache-and-network',
	});
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();

	const threadKey =
		parentComment.type === CommentType.INLINE
			? (parentComment.location as CommentLocation).inlineMarkerRef ?? ''
			: parentComment.id;

	const selectedAnnotation = {
		id: threadKey || '',
		type: AnnotationTypes.INLINE_COMMENT,
	};

	// State
	const [isThreadHovered, setIsThreadHovered] = useState<boolean>(false);
	const [isThreadPressed, setIsThreadPressed] = useState<boolean>(false);
	const [hoveredCommentId, setHoveredCommentId] = useState<string | undefined>(undefined);
	const [isAnnotatedTextPressed, setAnnotatedTextPressed] = useState(false);
	const [commentIsFocused, setCommentIsFocused] = useState<boolean>(false);

	// Hook dependent constants
	const isCurrentSelectedComment = currentlySelectedThreadKey === threadKey;

	const { editCommentId: editCommentQueryId } = useInlineCommentQueryParams();

	const { canAddComments } = useAddCommentPermissionCheck(pageInfo?.id ?? '');
	const { updateUnreadCommentsListState } = useUnreadCommentsActions();

	const { commentService } = useCommentService();

	const handleSelectThread = useCallback(
		(selectedThreadKey?: string) => {
			// Only open comments are visible in content types
			if (currentView !== ViewValues.RESOLVED) {
				commentService.handleSelectThread(selectedThreadKey);
			}
		},
		[commentService, currentView],
	);

	const handleCommentThreadHoverProps: CommentThreadMouseEnterParams = useMemo(
		() => ({
			threadKey,
			setIsThreadHovered,
			setHoveredCommentId,
			pageMode,
			eventEmitter,
		}),
		[threadKey, setIsThreadHovered, setHoveredCommentId, pageMode, eventEmitter],
	);

	const handleMouseEnter = useCallback(
		(id?: string) => {
			handleCommentThreadMouseEnter({
				...handleCommentThreadHoverProps,
				id,
			});
		},
		[handleCommentThreadHoverProps],
	);

	const handleMouseLeave = useCallback(() => {
		handleCommentThreadMouseLeave(handleCommentThreadHoverProps);
	}, [handleCommentThreadHoverProps]);

	const handleFocus = (event: React.FocusEvent) => {
		event.stopPropagation();
		setCommentIsFocused(true);
	};

	const handleBlur = (event: React.FocusEvent) => {
		if (!event.relatedTarget) {
			return;
		}
		if (!event.currentTarget.contains(event.relatedTarget as Node)) {
			setCommentIsFocused(false);
		}
	};

	const getBackgroundStyle = () => {
		if (isCurrentSelectedComment && !fg('confluence_frontend_comments_panel_v2')) {
			return isCurrentSelectedCommentStyles;
		}
		if (isCurrentSelectedComment && fg('confluence_frontend_comments_panel_v2')) {
			return isCurrentUserSelectedCommentStyles;
		}
		if (isThreadHovered && !fg('confluence_frontend_comments_panel_v2')) {
			return isThreadHoveredStyles;
		}
		if (isThreadPressed && !isAnnotatedTextPressed && fg('confluence_frontend_comments_panel_v2')) {
			return isThreadPressedStyles;
		}
		return undefined;
	};

	const userActionBackgroundStyles = getBackgroundStyle();

	const unreadParentCommentAlreadyMarkedAsReadRef = useRef(false);

	if (
		isCurrentSelectedComment &&
		!unreadParentCommentAlreadyMarkedAsReadRef.current &&
		parentComment.isUnread
	) {
		setCommentsInThreadToBeMarkedAsRead({
			threadKey,
			commentIds: [parentComment.id],
			commentType: parentComment.type,
		});
		unreadParentCommentAlreadyMarkedAsReadRef.current = true;
		updateUnreadCommentsListState((prev) => {
			return prev.filter((unreadComment) => {
				if (unreadComment.id === parentComment.id) {
					return false;
				}
				return true;
			});
		});
	}

	const isCommentRemovedByAnotherUser = parentComment?.wasRemovedByAnotherUser;
	const isResolvedByAnotherUser =
		isCommentRemovedByAnotherUser &&
		isCommentRemovedByAnotherUser === CommentActionType.RESOLVE_COMMENT_THREAD;

	// if comment thread is resolved, mark it as read exactly once
	useEffect(() => {
		if (isResolvedByAnotherUser) {
			const unreadCommentIds: string[] = [];

			if (parentComment.isUnread) {
				unreadCommentIds.push(parentComment.id);
			}

			const unreadReplies = parentComment.replies.filter((reply) => reply.isUnread);
			if (unreadReplies.length > 0) {
				unreadCommentIds.push(...unreadReplies.map((reply) => reply.id));
			}

			if (unreadCommentIds.length > 0) {
				setCommentsInThreadToBeMarkedAsRead({
					threadKey,
					commentIds: unreadCommentIds,
					commentType: parentComment.type,
				});
				updateUnreadCommentsListState((prev) => {
					const unreadCommentIdsSet = new Set(unreadCommentIds);
					return prev.filter((unreadComment) => !unreadCommentIdsSet.has(unreadComment.id));
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleCommentThreadMouseDown = () => {
		if (parentComment.type === CommentType.GENERAL) {
			return;
		}
		const isActiveInlineComment =
			parentComment.type === CommentType.INLINE &&
			!parentComment.wasRemovedByAnotherUser &&
			'inlineResolveProperties' in parentComment.location &&
			parentComment.location.inlineResolveProperties?.resolved === false;

		if (
			isActiveInlineComment &&
			(!isCurrentSelectedComment || !fg('cc_comments_skip_select_start_if_already_selected'))
		) {
			selectInlineCommentCompoundExperience.selectAnnotation.start({
				pageClass,
				annotationId: (parentComment.location as CommentLocation).inlineMarkerRef!,
				entryPoint: 'sidebar',
			});
		}

		setIsThreadPressed(true);
		!fg('confluence-frontend-duplicate-selected-thread') &&
			setCurrentlySelectedThreadKey(threadKey);
	};

	const canAddCommentsToCommentThread = canAddComments && currentView !== ViewValues.RESOLVED;

	const handleCommentThreadClick = (event: React.MouseEvent) => {
		handleCommentThreadSelection({
			parentComment,
			pageMode,
			scrollCommentsPanelToThread,
			setCurrentlySelectedThreadKey,
			eventEmitter,
			event,
			handleSelectThread,
		});
	};

	const commentThreadRef = useRef<HTMLLIElement>(null);

	useEffect(() => {
		if (
			isCurrentSelectedComment &&
			commentThreadRef.current &&
			fg('confluence_frontend_comments_a11y_kb')
		) {
			commentThreadRef.current.focus();
		}
	}, [isCurrentSelectedComment]);

	return (
		<>
			{shouldRenderCommentTypeSeparator &&
				fg('confluence-frontend-comments-panel-design-update') && (
					<Flex
						xcss={commentTypeSeperatorStyle}
						as="li"
						testId="comment-section-separator"
						alignItems="center"
					>
						<PageIcon
							testId="general-comment-page-icon"
							color={token('color.icon.subtlest')}
							label={formatMessage(i18n.commentTypeSeperatorText)}
						/>
						<Text color="color.text.subtlest" weight="medium">
							{formatMessage(i18n.commentTypeSeperatorText)}
						</Text>
					</Flex>
				)}
			{/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events, @atlassian/a11y/interactive-element-not-keyboard-focusable */}
			<Box
				// TODO - COMMENTS-4934: Clean up confluence_frontend_comments_a11y_kb FG
				{...(fg('confluence_frontend_comments_a11y_kb')
					? {
							role: 'button',
							tabIndex: 0,
							'aria-label': formatMessage(i18n.contentAriaLabelWithName, {
								name: parentComment.author?.displayName || formatMessage(i18n.anonymous),
							}),
						}
					: {})}
				id={`comment-thread-${threadKey}-container`}
				testId={`comment-thread-${threadKey}-container`}
				xcss={[
					commentThreadBoxStyles,
					userActionBackgroundStyles,
					fg('confluence_frontend_comments_panel_v2') && newCommentThreadBoxStyles,
				]}
				data-selected-comment-thread={
					isCurrentSelectedComment && (parentComment.location as CommentLocation)?.inlineMarkerRef
						? (parentComment.location as CommentLocation).inlineMarkerRef
						: undefined
				}
				as="li"
				onClick={handleCommentThreadClick}
				onMouseDown={handleCommentThreadMouseDown}
				onMouseUp={() => setIsThreadPressed(false)}
				onFocus={handleFocus}
				onBlur={handleBlur}
				ref={commentThreadRef}
			>
				<Box
					testId={`comment-thread-${parentComment.id}-parent`}
					xcss={commentBodyBoxStyles}
					onMouseEnter={() =>
						fg('confluence_frontend_comments_panel_v2')
							? handleMouseEnter(parentComment.id)
							: undefined
					}
					onMouseLeave={fg('confluence_frontend_comments_panel_v2') ? handleMouseLeave : undefined}
				>
					<Comment
						comment={parentComment}
						supportedTopLevelActions={supportedTopLevelActions}
						pageInfo={pageInfo}
						pageMode={pageMode}
						isUnread={
							!commentsMarkedAsRead[parentComment.type][threadKey]?.has(parentComment.id) &&
							parentComment.isUnread
						}
						isHovered={
							hoveredCommentId === parentComment.id ||
							(commentIsFocused && fg('confluence_frontend_comments_a11y_kb'))
						}
						threadKey={threadKey}
						flags={flags}
						isAnnotatedTextPressed={isAnnotatedTextPressed}
						setAnnotatedTextPressed={setAnnotatedTextPressed}
						hideBranchingStyle={!parentComment.isOpen && parentComment.replies.length === 0}
						canAddComments={canAddCommentsToCommentThread}
						isThreadHovered={isThreadHovered}
					/>
				</Box>
				<CommentReplies
					parentComment={parentComment}
					pageInfo={pageInfo}
					isCurrentSelectedComment={isCurrentSelectedComment}
					hoveredCommentId={hoveredCommentId ?? ''}
					selectedAnnotation={selectedAnnotation}
					editCommentQueryId={editCommentQueryId ?? ''}
					pageMode={pageMode}
					parentCommentId={parentComment.id}
					handleMouseEnter={handleMouseEnter}
					handleMouseLeave={handleMouseLeave}
					canAddComments={canAddCommentsToCommentThread}
				/>
				{/* per new design, we don't need to show the divider, clean up later */}
				{shouldRenderCommentThreadSeperator &&
					!fg('confluence-frontend-comments-panel') &&
					!fg('confluence_frontend_comments_panel_v2') && (
						<Box xcss={dividerStyle} testId={`comment-thread-${parentComment.id}-bottom-divider`} />
					)}
			</Box>
		</>
	);
};
